import React, { useMemo } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import BillReceiptPreview from "./BillReceiptPreview";
import { saleAPI } from "../../services/saleAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { toBillReceiptSale } from "../../utils/billReceipt.helper";
import type { SalesReportRow } from "../../services/reportAPI";

type Props = {
  open: boolean;
  saleRow: SalesReportRow | null;
  onClose: () => void;
};

export default function SaleBillDrawer({ open, saleRow, onClose }: Props) {
  const saleId = saleRow?.id || "";

  const { data: saleDetail, isLoading: saleLoading } = useQuery({
    queryKey: ["sale-bill-detail", saleId],
    queryFn: () => saleAPI.getSaleById(saleId),
    enabled: open && Boolean(saleId),
    staleTime: 60_000,
  });

  const salesPointId =
    saleDetail?.salesPointId ||
    (saleDetail as any)?.salesPoint?.id ||
    (saleRow as any)?.salesPointId ||
    saleRow?.salesPoint?.id;

  const embeddedSp = (saleDetail as any)?.salesPoint;

  const { data: salesPointFetched, isLoading: spLoading } = useQuery({
    queryKey: ["sale-bill-sp", salesPointId],
    queryFn: () => salesPointAPI.getSalesPointById(String(salesPointId)),
    enabled: open && Boolean(salesPointId) && !embeddedSp,
    staleTime: 5 * 60_000,
  });

  const salesPoint = embeddedSp || salesPointFetched || null;

  const bill = useMemo(() => {
    const merged = {
      ...(saleRow || {}),
      ...(saleDetail || {}),
      saleItems:
        (saleDetail as any)?.saleItems ||
        (saleDetail as any)?.items ||
        saleRow?.saleItems ||
        [],
      salesPoint: saleRow?.salesPoint,
    };
    return toBillReceiptSale(merged as Record<string, any>, salesPoint || null);
  }, [saleRow, saleDetail, salesPoint]);

  const loading = saleLoading || spLoading;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          p: 0,
          bgcolor: "#f3f3f3",
        },
        className: "sale-bill-drawer",
      }}
    >
      {saleRow && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Bill — {saleRow.saleNumber}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            className="sale-bill-print-root"
            sx={{ flex: 1, overflow: "auto", py: 1 }}
          >
            <BillReceiptPreview sale={bill} loading={loading} />
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading}
            >
              Print bill
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
